import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
  useCallback
} from 'react'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'
import { format, isSameDay, parseISO } from 'date-fns'

import {
  Avatar,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@material-ui/core'

import {
  AccessTime,
  ErrorOutline,
  Done,
  DoneAll,
  ExpandMore,
  Facebook,
  FileCopyOutlined,
  Instagram
} from '@material-ui/icons'

import WhatsMarked from 'react-whatsmarked'
import ModalImageCors from '../ModalImageCors'
import { Mutex } from 'async-mutex'
import {
  ArrowDownToLine,
  ArrowLeftRight,
  Ban,
  Clock,
  Forward,
  MessageSquareLock,
  Smile,
  UserMinus,
  Type,
  UserRound,
  Workflow,
  X
} from 'lucide-react'
import vCard from 'vcard-parser'
import { toast } from 'react-toastify'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import { generateColor } from '../../helpers/colorGenerator'
import { getInitials } from '../../helpers/getInitials'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import AudioPlayer from '../AudioPlayer'
import { AuthContext } from '../../context/Auth/AuthContext'
import { useStyles } from './style'
import MessageActionsMenu from '../MessageActionsMenu'
import MessageReactionsMenu from '../MessageReactionsMenu'
import useTicketHistory from '../../hooks/useTicketHistory'

import { getFileIconSrc } from '../../utils/fileIconMapper'
import { downloadFileFromUrl } from '../../utils/downloadFileFromUrl'
import { getTicketProtocol } from '../../utils/formatAudioDownloadName'

const loadPageMutex = new Mutex()

const reducer = (state, action) => {
  if (action.type === 'LOAD_MESSAGES') {
    const messages = action.payload
    const newMessages = []

    messages.forEach(message => {
      const messageIndex = state.findIndex(m => m.id === message.id)
      if (messageIndex !== -1) {
        state[messageIndex] = message
      } else {
        newMessages.push(message)
      }
    })

    return [...newMessages, ...state]
  }

  if (action.type === 'ADD_MESSAGE') {
    const newMessage = action.payload
    const messageIndex = state.findIndex(m => m.id === newMessage.id)

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage
    } else {
      state.push(newMessage)
    }
    if (newMessage.mediaType === 'reactionMessage' && newMessage.quotedMsgId) {
      const parentMessageIndex = state.findIndex(
        m => m.id === newMessage.quotedMsgId
      )

      if (parentMessageIndex !== -1) {
        const parentMsg = state[parentMessageIndex]
        const currentReplies = parentMsg.replies || []

        const replyIndex = currentReplies.findIndex(r => r.id === newMessage.id)

        if (replyIndex !== -1) {
          currentReplies[replyIndex] = newMessage
        } else {
          parentMsg.replies = [...currentReplies, newMessage]
        }
      }
    }

    // Remove mensagens de áudio otimistas enviadas pelo atendente quando a
    // mensagem real chega pelo socket, evitando duplicidade temporária.
    if (newMessage.fromMe && newMessage.mediaType === 'audio') {
      const now = new Date()
      return state.filter(
        m =>
          !(
            m.isOptimistic &&
            m.mediaType === 'audio' &&
            now - new Date(m.createdAt) < 60000
          )
      )
    }

    return [...state]
  }

  if (action.type === 'ADD_OPTIMISTIC_MESSAGE') {
    const optimisticMessage = action.payload
    const messageIndex = state.findIndex(m => m.id === optimisticMessage.id)

    if (messageIndex !== -1) {
      state[messageIndex] = optimisticMessage
    } else {
      state.push(optimisticMessage)
    }

    return [...state]
  }

  if (action.type === 'REMOVE_OPTIMISTIC_MESSAGE') {
    const messageId = action.payload
    return state.filter(m => m.id !== messageId)
  }

  if (action.type === 'UPDATE_MESSAGE') {
    const messageToUpdate = action.payload
    const messageIndex = state.findIndex(m => m.id === messageToUpdate.id)

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate
    }

    return [...state]
  }

  if (action.type === 'MERGE_MESSAGES') {
    const messages = action.payload

    messages.forEach(message => {
      const messageIndex = state.findIndex(m => m.id === message.id)
      if (messageIndex !== -1) {
        state[messageIndex] = message
      } else {
        const idx = state.findIndex(
          m => new Date(m.createdAt) > new Date(message.createdAt)
        )
        state.splice(idx < 0 ? state.length : idx, 0, message)
      }
    })

    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const SystemEventBadge = ({ event, classes }) => {
  const getEventConfig = note => {
    if (note.includes('🔄'))
      return {
        icon: <ArrowLeftRight size={14} />,
        label: 'Transferência',
        className: classes.transferDot
      }
    if (note.includes('🔴'))
      return {
        icon: <X size={14} />,
        label: 'Finalizado',
        className: classes.closedDot
      }
    if (note.includes('🟢'))
      return {
        icon: <UserRound size={14} />,
        label: 'Iniciado',
        className: classes.historyDot
      }
    if (note.includes('🟡'))
      return {
        icon: <Clock size={14} />,
        label: 'Nota',
        className: classes.noteDot
      }

    if (note.includes('👤'))
      return {
        icon: <UserMinus size={14} />,
        label: 'Desvinculado',
        className: classes.manualDot
      }

    return {
      icon: <Workflow size={14} />,
      label: 'Sistema',
      className: classes.manualDot
    }
  }

  const config = getEventConfig(event.note)
  const time = new Date(event.createdAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  })
  const cleanNoteText = event.note
    .replace(/^(🟢|🔄|🔴|🟡|ℹ️|👤)\s*/, '')
    .split('-')[0]

  return (
    <div
      className={classes.systemMsgContainer}
      role="status"
      aria-label={`Evento de sistema: ${config.label}`}
    >
      <Paper
        elevation={0}
        className={clsx(classes.systemEventPaper, config.className)}
      >
        {config.icon}
        <Typography variant="caption" style={{ fontWeight: 600 }}>
          {cleanNoteText} • {time}
        </Typography>
      </Paper>
    </div>
  )
}

const AudioMessageWithTranscription = ({ message, ticket, classes }) => {
  const [showTranscription, setShowTranscription] = useState(false)
  const transcriptionText = message.transcription || message.body || ''

  // Garante que o botão só apareça se houver texto de transcrição disponível
  // e se o áudio foi recebido de um contato (não enviado pelo usuário)
  const hasTranscriptionData = Boolean(
    !message.fromMe &&
    (message.transcription ||
      (message.body && !message.body.startsWith('Áudio')))
  )

  const handleToggleTranscription = useCallback(() => {
    setShowTranscription(prev => !prev)
  }, [])

  return (
    <div
      className={classes.audioMessageWrapper}
      aria-label="Mensagem de áudio"
    >
      <AudioPlayer
        audioUrl={message.mediaUrl}
        profileImageUrl={
          !message.fromMe ? message.contact?.profilePicUrl : null
        }
        isMessageRight={message.fromMe}
        fileName={message.body}
        ticket={ticket}
        message={message}
      />

      {hasTranscriptionData && (
        <div className={classes.transcriptionControlContainer}>
          <Button
            type="button"
            onClick={handleToggleTranscription}
            className={classes.transcriptionToggleButton}
            aria-expanded={showTranscription}
            aria-controls={`transcription-region-${message.id}`}
            aria-label={
              showTranscription
                ? 'Ocultar transcrição do áudio'
                : 'Transcrever áudio'
            }
          >
            <span>
              <Type size={16} />

              {showTranscription ? 'Ocultar Transcrição' : 'Transcrever Áudio'}
            </span>
          </Button>

          {showTranscription && (
            <section
              id={`transcription-region-${message.id}`}
              className={clsx(classes.transcriptionContent, {
                [classes.transcriptionContentRight]: message.fromMe
              })}
              role="region"
              aria-label="Texto transcrito do áudio"
            >
              <WhatsMarked>{transcriptionText}</WhatsMarked>
            </section>
          )}
        </div>
      )}
    </div>
  )
}

const MessagesList = forwardRef(({
  ticket,
  ticketId,
  isGroup,
  markAsRead,
  onOpenNewTicketForContact
}, ref) => {
  const classes = useStyles({ isVCardItem: false })
  const history = useHistory()

  const [messagesList, dispatch] = useReducer(reducer, [])
  const messagesListRef = useRef(messagesList)

  useImperativeHandle(ref, () => ({
    addOptimisticMessage: message => {
      dispatch({ type: 'ADD_OPTIMISTIC_MESSAGE', payload: message })
      scrollToBottom()
    },
    removeOptimisticMessage: messageId => {
      dispatch({ type: 'REMOVE_OPTIMISTIC_MESSAGE', payload: messageId })
    }
  }))
  useEffect(() => {
    messagesListRef.current = messagesList
  }, [messagesList])
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [ticketsSummary, setTicketsSummary] = useState([])
  const scrollRef = useRef()

  const currentTicketId = useRef(ticketId)
  const [contactPresence, setContactPresence] = useState('available')
  const [hoveredMessageId, setHoveredMessageId] = useState(null)

  const [selectedReactionMessage, setSelectedReactionMessage] = useState({})
  const [reactionsAnchorEl, setReactionsAnchorEl] = useState(null)
  const reactionsMenuOpen = Boolean(reactionsAnchorEl)

  const [selectedActionMessage, setSelectedActionMessage] = useState({})
  const [selectedActionMessageData, setSelectedActionMessageData] = useState({})
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null)
  const actionsMenuOpen = Boolean(actionsAnchorEl)

  const socketManager = useContext(SocketContext)
  const { user } = useContext(AuthContext)
  const { history: ticketEvents } = useTicketHistory(ticket, ticket.contact)

  const getIncomingMessageSignature = message => {
    if (!message) return ''

    if (isGroup) {
      return message.contact?.name || ''
    }

    return (message.contactSignature || '').trim()
  }

  function loadData(incrementPage = false) {
    setLoading(true)
    const thisPageNumber = incrementPage ? pageNumber + 1 : 1
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === undefined) return
        try {
          const { data } = await api.get('/messages/' + ticketId, {
            params: { pageNumber: thisPageNumber, markAsRead }
          })

          if (currentTicketId.current === ticketId) {
            dispatch({ type: 'LOAD_MESSAGES', payload: data.messages })
            setHasMore(data.hasMore)
            if (Array.isArray(data.ticketsSummary)) {
              setTicketsSummary(data.ticketsSummary)
            }
            setLoading(false)
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom()
          }
        } catch (err) {
          setLoading(false)
          toastError(err)
        }
      }
      fetchMessages()
      setPageNumber(thisPageNumber)
    }, 500)
    return () => {
      clearTimeout(delayDebounceFn)
    }
  }

  function refreshMessagesList() {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
    setHasMore(false)
    loadData()
  }

  const allTimelineItems = useMemo(() => {
    const eventsAsMessages = ticketEvents
      .filter(event => !event.note.includes('[Bot]'))
      .map(event => ({
        ...event,
        isSystemEvent: true
      }))

    const combined = [...messagesList, ...eventsAsMessages]

    return combined.sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    )
  }, [messagesList, ticketEvents])

  const ticketDividerIds = useMemo(() => {
    const dividerIds = new Set()
    const hasHistory = messagesList.some(
      m => m.ticketId && String(m.ticketId) !== String(ticketId)
    )
    if (!hasHistory) return dividerIds

    let previousTicketId = null
    allTimelineItems.forEach(item => {
      if (item.isSystemEvent || !item.ticketId) return
      if (item.ticketId !== previousTicketId) {
        dividerIds.add(item.id)
        previousTicketId = item.ticketId
      }
    })
    return dividerIds
  }, [allTimelineItems, messagesList, ticketId])

  const protocolByTicketId = useMemo(() => {
    const map = {}
    if (ticket?.id) {
      map[ticket.id] = getTicketProtocol(ticket)
    }
    ticketsSummary.forEach(t => {
      map[t.id] = getTicketProtocol(t)
    })
    return map
  }, [ticket, ticketsSummary])

  useEffect(() => {
    let timeoutId = null
    const init = async () => {
      dispatch({ type: 'RESET' })
      setContactPresence('available')
      currentTicketId.current = ticketId

      await loadPageMutex.runExclusive(async () => {
        timeoutId = loadData()
      })
    }
    init()
    return () => {
      if (timeoutId) clearTimeout(timeoutId) // Limpa busca antiga se o ticket mudar ou fechar
    }
  }, [ticketId])

  useEffect(() => {
    if (!ticket.id) {
      return
    }
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)
    const onConnect = () => {
      socket.emit('joinChatBox', `${ticket.id}`)
    }
    socketManager.onConnect(onConnect)
    const onAppMessage = data => {
      if (data.message.ticketId === currentTicketId.current) {
        setContactPresence('available')
        if (data.action === 'create') {
          dispatch({ type: 'ADD_MESSAGE', payload: data.message })
          if (data.message.mediaType !== 'reactionMessage') {
            scrollToBottom()
          }
        }
        if (data.action === 'update') {
          dispatch({ type: 'UPDATE_MESSAGE', payload: data.message })
        }
      }
    }

    socket.on(`company-${companyId}-appMessage`, onAppMessage)
    socket.on('wsRefreshRequired', refreshRequired => {
      if (!refreshRequired || !currentTicketId.current) {
        return
      }

      loadPageMutex.runExclusive(async () => {
        const currentList = messagesListRef.current
        if (currentList.length > 0) {
          const maxUpdatedAt = currentList.reduce(
            (max, msg) => (msg.updatedAt > max ? msg.updatedAt : max),
            currentList[0].updatedAt
          )

          try {
            const { data } = await api.get(
              '/messages/' + currentTicketId.current,
              {
                params: { minUpdatedAt: maxUpdatedAt }
              }
            )
            const currentIds = new Set(currentList.map(m => m.id))
            const newMessages = data.messages.filter(m => !currentIds.has(m.id))
            let isAtBottom = false
            let newestMessage = null

            if (newMessages.length > 0) {
              const { scrollTop, clientHeight, scrollHeight } =
                scrollRef.current
              isAtBottom =
                scrollTop + clientHeight >= scrollHeight - clientHeight / 4
              newestMessage = newMessages.reduce((latest, msg) =>
                new Date(msg.createdAt) > new Date(latest.createdAt)
                  ? msg
                  : latest
              )
              newestMessage.bottomStick =
                (!isAtBottom && !newestMessage.fromMe) || undefined
            }

            dispatch({ type: 'MERGE_MESSAGES', payload: data.messages })

            if (newMessages.length > 0) {
              if (
                (isAtBottom || newestMessage.fromMe) &&
                newestMessage.mediaType !== 'reactionMessage'
              ) {
                scrollToBottom()
              }
            }
          } catch (err) {
            toastError(err)
          }
        } else {
          refreshMessagesList()
        }
      })
    })
    socket.on(`company-${companyId}-presence`, data => {
      const { scrollTop, clientHeight, scrollHeight } = scrollRef.current
      const isAtBottom =
        scrollTop + clientHeight >= scrollHeight - clientHeight / 4
      if (data?.ticketId === ticket.id) {
        setContactPresence(data.presence)
        if (['composing', 'recording'].includes(data.presence)) {
          if (isAtBottom) {
            scrollToBottom()
          }
        }
      }
    })

    return () => {
      socket.disconnect()
    }
  }, [ticketId, ticket, socketManager])

  const loadMore = async () => {
    await loadPageMutex.runExclusive(async () => {
      loadData(true)
    })
  }

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }

  const handleScroll = e => {
    if (!hasMore) return
    const { scrollTop } = e.currentTarget

    if (scrollTop === 0) {
      document.getElementById('messagesList').scrollTop = 1
    }
    if (loading) {
      return
    }
    if (scrollTop < 50) {
      loadMore()
    }
  }

  const handleOpenReactionsMenu = (e, message) => {
    setReactionsAnchorEl(e.currentTarget)
    setSelectedReactionMessage(message)
  }

  const handleCloseReactionsMenu = () => {
    setReactionsAnchorEl(null)
  }

  const handleOpenActionsMenu = (e, message, data) => {
    setActionsAnchorEl(e.currentTarget)
    setSelectedActionMessage(message)
    setSelectedActionMessageData(data)
  }

  const handleCloseActionsMenu = () => {
    setActionsAnchorEl(null)
  }

  const formatFileSize = bytes => {
    if (bytes === null || bytes === undefined) return 'Desconhecido'
    const size0 = Number(bytes)
    if (isNaN(size0)) return 'Desconhecido'

    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let size = size0
    let unitIndex = 0

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }

    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
  }

  const getDocumentIcon = (message, document) => {
    const fileName = document?.fileName || message.body || ''
    const extension = fileName.split('.').pop().toLowerCase()

    const iconSrc = getFileIconSrc(fileName, message.mediaType)

    return (
      <img
        src={iconSrc}
        alt={`Arquivo .${extension}`}
        className={classes.documentIcon}
      />
    )
  }

  const checkMessageMedia = (message, data) => {
    const document =
      data?.message?.documentMessage ||
      data?.message?.documentWithCaptionMessage?.message?.documentMessage
    if (!document && message.mediaType === 'image') {
      return (
        <>
          {
            <ModalImageCors
              imageUrl={message.mediaUrl}
              isDeleted={message.isDeleted}
            />
          }
          <>
            <div
              className={[
                clsx({
                  [classes.textContentItemDeleted]: message.isDeleted,
                  [classes.textContentItem]: !message.isDeleted
                })
              ]}
            >
              {data?.message?.imageMessage?.caption && (
                <>
                  <WhatsMarked>{data.message.imageMessage.caption}</WhatsMarked>
                </>
              )}
            </div>
          </>
        </>
      )
    }
    // if (!document && message.mediaType === 'audio') {
    //   return (
    //     <>
    //       <AudioPlayer
    //         audioUrl={message.mediaUrl}
    //         profileImageUrl={
    //           !message.fromMe ? message.contact?.profilePicUrl : null
    //         }
    //         isMessageRight={message.fromMe}
    //         fileName={message.body}
    //         ticket={ticket}
    //         message={message}
    //       />
    //       {!message.fromMe &&
    //         message.body &&
    //         !message.body.startsWith('Áudio') && (
    //           <div
    //             className={clsx(classes.mediaDescription, {
    //               [classes.mediaDescriptionRight]: message.fromMe
    //             })}
    //           >
    //             {message.body}
    //           </div>
    //         )}
    //     </>
    //   )
    // }

    if (!document && message.mediaType === 'audio') {
      console.log('Mensagem', message)
      return (
        <AudioMessageWithTranscription
          message={message}
          ticket={ticket}
          classes={classes}
        />
      )
    }

    if (!document || message.mediaType === 'video') {
      return (
        <video
          className={classes.messageVideo}
          src={message.mediaUrl}
          controls
        />
      )
    } else {
      const fileName = document?.fileName || message.body

      return (
        <>
          <div className={classes.downloadMedia}>
            <div className={classes.downloadMediaContent}>
              {getDocumentIcon(message, document)}
              <div className={classes.downloadMediaInfo}>
                <span className={classes.downloadMediaTitle}>
                  {document?.fileName || message.body}
                </span>
                <span className={classes.downloadMediaSize}>
                  {formatFileSize(document?.fileLength)}
                </span>
              </div>
              <IconButton
                onClick={async event => {
                  event.preventDefault()
                  event.stopPropagation()

                  try {
                    await downloadFileFromUrl(message.mediaUrl, fileName)
                  } catch (error) {
                    toastError(error)
                  }
                }}
              >
                <ArrowDownToLine />
              </IconButton>
            </div>
          </div>
          {message.body !== document?.fileName && !isVCard(message.body) && (
            <>
              <div
                className={[
                  clsx({
                    [classes.textContentItemDeleted]: message.isDeleted
                  })
                ]}
              >
                <WhatsMarked>{message.body}</WhatsMarked>
              </div>
            </>
          )}
        </>
      )
    }
  }

  const renderMessageAck = message => {
    if (message.ack === -1) {
      return <ErrorOutline fontSize="small" className={classes.ackIcons} />
    }
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />
    }
    if (message.ack === 3) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />
    }
    if (message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneReadIcon} />
    }
  }

  const renderDailyTimestamps = (item, index) => {
    const ticketDivider = !item.isSystemEvent &&
      ticketDividerIds.has(item.id) && (
        <div
          className={classes.ticketDivider}
          key={`ticket-divider-${item.id}`}
          role="separator"
        >
          <span className={classes.ticketDividerText}>
            {protocolByTicketId[item.ticketId]
              ? `Protocolo: ${protocolByTicketId[item.ticketId]}`
              : `#${item.ticketId}`}
          </span>
        </div>
      )
    const dailyTimestamp = renderDailyTimestampBadge(item, index)

    if (!ticketDivider) return dailyTimestamp

    return (
      <>
        {dailyTimestamp}
        {ticketDivider}
      </>
    )
  }

  const renderDailyTimestampBadge = (item, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${item.id || index}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(allTimelineItems[index].createdAt), 'dd/MM/yy')}
          </div>
        </span>
      )
    }

    if (index < allTimelineItems.length) {
      let itemDay = parseISO(allTimelineItems[index].createdAt)
      let previousItemDay = parseISO(allTimelineItems[index - 1].createdAt)

      if (!isSameDay(itemDay, previousItemDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${item.id || index}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(allTimelineItems[index].createdAt), 'dd/MM/yy')}
            </div>
          </span>
        )
      }
    }
  }

  const renderMessageDivider = (item, index) => {
    if (index < allTimelineItems.length && index > 0) {
      if (
        allTimelineItems[index].isSystemEvent ||
        allTimelineItems[index - 1].isSystemEvent
      ) {
        return null
      }

      let messageUser = allTimelineItems[index].fromMe
      let previousMessageUser = allTimelineItems[index - 1].fromMe

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${item.id}`}></span>
        )
      }
    }
    return null
  }

  const scrollToMessage = id => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })

      element.classList.add(classes.messageHighlighted)

      setTimeout(() => {
        element.classList.remove(classes.messageHighlighted)
      }, 2000)
    }
  }

  const getQuotedMessageText = quotedMsg => {
    if (!quotedMsg?.body && quotedMsg?.mediaUrl) {
      return '📎 ' + quotedMsg.mediaUrl.split('/').pop()
    }

    if (isVCard(quotedMsg?.body)) {
      return '🪪'
    }

    if (quotedMsg?.mediaType === 'audio') {
      return 'Áudio'
    }

    return quotedMsg?.body
  }

  const renderQuotedMessage = message => {
    const data = JSON.parse(message.quotedMsg.dataJson)

    const thumbnail = data?.message?.imageMessage?.jpegThumbnail
    const mediaUrl =
      message.quotedMsg?.mediaType === 'image'
        ? message.quotedMsg.mediaUrl
        : null
    const imageUrl = thumbnail
      ? 'data:image/png;base64, ' + thumbnail
      : mediaUrl
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe
        })}
        onClick={() => scrollToMessage(message.quotedMsg.id)}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {getIncomingMessageSignature(message.quotedMsg) ||
                message.quotedMsg?.contact?.name}
            </span>
          )}
          <WhatsMarked>{getQuotedMessageText(message.quotedMsg)}</WhatsMarked>
        </div>
        {imageUrl && (
          <img
            className={classes.quotedThumbnail}
            src={imageUrl}
            alt={imageUrl}
          />
        )}
      </div>
    )
  }

  const renderReplies = (replies, isFromMe) => {
    if (!replies || replies.length === 0) return null

    const reactionReplies = replies.filter(
      reply => reply?.mediaType === 'reactionMessage'
    )

    if (reactionReplies.length === 0) return null

    const groupedReactions = reactionReplies.reduce((acc, reply) => {
      const emoji = reply.body
      if (!acc[emoji]) {
        acc[emoji] = {
          emoji: emoji,
          count: 0,
          users: []
        }
      }
      acc[emoji].count++
      if (reply.contact?.name) {
        acc[emoji].users.push(reply.contact.name)
      }
      return acc
    }, {})

    const sortedReactions = Object.values(groupedReactions).sort(
      (a, b) => b.count - a.count
    )

    return (
      <div
        className={clsx(
          classes.reactionsContainer,
          isFromMe
            ? classes.reactionsContainerRight
            : classes.reactionsContainerLeft
        )}
      >
        <div
          className={clsx(
            classes.reactions,
            isFromMe ? classes.reactionsRight : classes.reactionsLeft
          )}
        >
          {sortedReactions.map((reaction, index) => (
            <Tooltip
              key={index}
              title={
                reaction.users.length > 0
                  ? reaction.users.join(', ')
                  : `${reaction.count} ${reaction.count > 1 ? 'reações' : 'reação'
                  }`
              }
              placement="top"
              arrow
            >
              <div className={classes.reactionItem}>
                <span>{reaction.emoji}</span>
                {reaction.count > 1 && (
                  <span className={classes.reactionCount}>
                    {reaction.count}
                  </span>
                )}
              </div>
            </Tooltip>
          ))}
        </div>
      </div>
    )
  }

  const getMessageErrorData = message => {
    if (!message?.error) {
      return null
    }

    if (typeof message.error === 'string') {
      const text = message.error.trim()
      if (!text) {
        return null
      }
      return {
        code: 'ERROR',
        message: text,
        title: `[ERROR] ${text}`
      }
    }

    const errorCode =
      typeof message.error.code === 'string' && message.error.code.trim()
        ? message.error.code.trim()
        : 'ERROR'

    const errorMessage =
      typeof message.error.message === 'string' && message.error.message.trim()
        ? message.error.message.trim()
        : 'Unknown error'

    return {
      code: errorCode,
      message: errorMessage,
      title: `[${errorCode}] ${errorMessage}`
    }
  }

  const renderLinkPreview = message => {
    const data = JSON.parse(message.dataJson)

    const title = data?.message?.extendedTextMessage?.title
    const description = data?.message?.extendedTextMessage?.description
    const canonicalUrl = data?.message?.extendedTextMessage?.canonicalUrl
    const url = canonicalUrl && new URL(canonicalUrl)

    if (!title && !description && !url) {
      return <></>
    }

    const thumbnail = data?.message?.extendedTextMessage?.jpegThumbnail
    const imageUrl = thumbnail ? 'data:image/png;base64, ' + thumbnail : ''
    return (
      <a
        href={canonicalUrl}
        className={classes.linkPreviewAnchor}
        target="_blank"
        rel="noreferrer"
      >
        <div
          className={clsx(classes.quotedContainerLeft, {
            [classes.quotedContainerRight]: message.fromMe
          })}
        >
          {/* <div className={classes.quotedMsg}>
            {title && <div className={classes.linkPreviewTitle}>{title}</div>}
            {description && (
              <div className={classes.linkPreviewDescription}>
                {description}
              </div>
            )}
            {url?.hostname && (
              <div className={classes.linkPreviewUrl}>{url.hostname}</div>
            )}
          </div> */}
          {!message.thumbnailUrl && imageUrl && (
            <img className={classes.quotedThumbnail} src={imageUrl} />
          )}
        </div>
      </a>
    )
  }

  const formatVCardN = n => {
    return (
      (n[0] ? n[0] + ' ' : '') +
      (n[1] ? n[1] + ' ' : '') +
      (n[2] ? n[2] + ' ' : '') +
      (n[3] ? n[3] + ' ' : '') +
      (n[4] ? n[4] + ' ' : '')
    )
  }

  const isVCard = message => {
    if (message.startsWith('{"ticketzvCard":')) {
      return true
    }
    if (message.startsWith('BEGIN:VCARD') && message.includes('END:VCARD')) {
      return true
    }
    return false
  }

  const stringOrFirstElement = data => {
    if (!data) {
      return ''
    }
    if (Array.isArray(data)) {
      return data[0]
    }
    return data
  }

  const renderVCard = vcardData => {
    let cardArray = []

    if (vcardData.startsWith('{"ticketzvCard":')) {
      const parsedData = JSON.parse(vcardData)?.ticketzvCard
      if (!parsedData || !Array.isArray(parsedData)) {
        return <div>Invalid VCARD data</div>
      }
      cardArray = parsedData
    } else if (
      vcardData.startsWith('BEGIN:VCARD') &&
      vcardData.includes('END:VCARD')
    ) {
      cardArray = [{ vcard: vcardData }]
    } else {
      return <div>Invalid VCARD format</div>
    }

    return cardArray.map((item, index) => {
      const message = item?.vcard
      if (!message) {
        return <React.Fragment key={index}></React.Fragment>
      }

      const parsedVCard = vCard.parse(message)
      console.debug('vCard data:', { message, parsedVCard })

      const name = stringOrFirstElement(
        parsedVCard['X-WA-BIZ-NAME']?.[0]?.value ||
        parsedVCard.fn?.[0]?.value ||
        formatVCardN(parsedVCard.n?.[0]?.value)
      )
      const description = stringOrFirstElement(
        parsedVCard['X-WA-BIZ-DESCRIPTION']?.[0]?.value || ''
      )
      const number = stringOrFirstElement(parsedVCard?.tel?.[0]?.value)
      const metaNumber =
        parsedVCard?.tel?.[0]?.meta?.waid?.[0] || number || `unknown-${index}`

      const pixType = stringOrFirstElement(
        parsedVCard['X-TICKETZ-PIX-TYPE']?.[0]?.value || ''
      )
      const pixKeyRaw = stringOrFirstElement(
        parsedVCard['X-TICKETZ-PIX-KEY']?.[0]?.value || ''
      )
      const pixAccountName = stringOrFirstElement(
        parsedVCard['X-TICKETZ-PIX-NAME']?.[0]?.value || name
      )

      const pixTypeLabelMap = {
        cpf: 'CPF',
        cnpj: 'CNPJ',
        email: 'E-mail',
        phone: 'Telefone',
        random: 'Chave Aleatória'
      }

      const fallbackDescriptionMatch = description.match(
        /^(CPF|CNPJ|E-mail|Email|Telefone|Chave Aleat[oó]ria)\s*:\s*(.+)$/i
      )

      const pixTypeFromDescription = fallbackDescriptionMatch
        ? fallbackDescriptionMatch[1].toLowerCase()
        : ''

      const normalizePixType = rawType => {
        const value = (rawType || '').toLowerCase()
        if (value === 'cpf') return 'cpf'
        if (value === 'cnpj') return 'cnpj'
        if (value === 'e-mail' || value === 'email') return 'email'
        if (value === 'telefone') return 'phone'
        if (value === 'chave aleatória' || value === 'chave aleatoria')
          return 'random'
        return ''
      }

      const normalizedPixType =
        normalizePixType(pixType) || normalizePixType(pixTypeFromDescription)

      const pixKey = pixKeyRaw || fallbackDescriptionMatch?.[2]?.trim() || ''
      const pixTypeLabel = pixTypeLabelMap[normalizedPixType] || 'Chave Pix'
      const isPixCard = Boolean(pixKey)

      const handleCopyPixKey = async e => {
        e.stopPropagation()
        if (!pixKey) return
        try {
          await navigator.clipboard.writeText(pixKey)
          toast.success('Chave Pix copiada!')
        } catch (error) {
          toastError(error)
        }
      }

      if (isPixCard) {
        return (
          <div key={metaNumber}>
            <div className={classes.pixCardContainer}>
              <div className={classes.pixCardHeader}>
                <Avatar
                  className={classes.vCardAvatar}
                  style={{
                    backgroundColor: generateColor(metaNumber),
                    width: 48,
                    height: 48,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '18px'
                  }}
                >
                  {getInitials(pixAccountName)}
                </Avatar>

                <div className={classes.vCardInfo}>
                  <Typography
                    component="h3"
                    variant="body1"
                    className={classes.vCardName}
                  >
                    {`Nome: ${pixAccountName}`}
                  </Typography>
                  <Typography
                    component="p"
                    variant="body2"
                    className={classes.vCardDescription}
                  >
                    {`Tipo da Chave: ${pixTypeLabel}`}
                  </Typography>
                  <Typography
                    component="p"
                    variant="body2"
                    className={classes.vCardDescription}
                  >
                    {`Chave Pix: ${pixKey}`}
                  </Typography>
                </div>
              </div>

              <div className={classes.pixCardAction}>
                <button
                  className={classes.pixCardActionButton}
                  onClick={handleCopyPixKey}
                >
                  <FileCopyOutlined className={classes.pixCardActionIcon} />
                  <Typography
                    component="span"
                    variant="body2"
                    className={classes.pixCardActionText}
                  >
                    Copiar chave Pix
                  </Typography>
                </button>
              </div>
            </div>
          </div>
        )
      }

      const handleAddContact = e => {
        e.stopPropagation()
        // Navegar para a página de contatos
        history.push('/contacts')

        // Após um pequeno delay, simular o clique no botão de adicionar contato
        setTimeout(() => {
          // Disparar um evento customizado com os dados do contato
          window.dispatchEvent(
            new CustomEvent('addContactFromVCard', {
              detail: {
                name: name,
                number: number.replace(/\D/g, ''),
                email: '',
                site: '',
                pessoa: '',
                disableBot: false
              }
            })
          )
        }, 100)
      }

      const handleCreateTicket = async e => {
        e.stopPropagation()

        const contactData = {
          name: name || number,
          number: number.replace(/\D/g, ''),
          email: '',
          site: '',
          pessoa: '',
          disableBot: false,
          preselectedWhatsappId: ticket?.whatsappId
        }

        if (onOpenNewTicketForContact) {
          onOpenNewTicketForContact(contactData)
          return
        }

        try {
          // Primeiro, verificar se o contato já existe
          const { data: contactsData } = await api.get('/contacts/', {
            params: { searchParam: number.replace(/\D/g, ''), pageNumber: 1 }
          })

          let contactId = null

          if (contactsData.contacts && contactsData.contacts.length > 0) {
            // Contato já existe
            contactId = contactsData.contacts[0].id

            // Verificar se já há um ticket aberto para este contato
            try {
              console.log(
                'Verificando tickets existentes para contactId:',
                contactId
              )
              const { data: ticketsData } = await api.get('/tickets/', {
                params: {
                  contactId: contactId,
                  status: 'open',
                  pageNumber: 1
                }
              })

              console.log('Tickets encontrados:', ticketsData)

              if (ticketsData.tickets && ticketsData.tickets.length > 0) {
                // Já existe um ticket aberto, navegar para ele
                const existingTicket = ticketsData.tickets[0]
                console.log('Ticket existente encontrado:', existingTicket)
                history.push(
                  `/tickets/${existingTicket.uuid || existingTicket.id}`
                )
                return
              }
            } catch (ticketCheckError) {
              console.log(
                'Erro ao verificar tickets existentes:',
                ticketCheckError
              )
              // Continue com a criação do ticket mesmo se houver erro na verificação
            }
          } else {
            // Criar novo contato
            const contactData = {
              name: name,
              number: number.replace(/\D/g, ''),
              email: '',
              site: '',
              pessoa: '',
              disableBot: false
            }

            const { data: newContact } = await api.post(
              '/contacts',
              contactData
            )
            contactId = newContact.id
          }

          // Criar ticket para o contato
          const ticketData = {
            contactId: contactId,
            queueId: null,
            userId: user.id,
            status: 'open'
          }

          console.log('Criando ticket com dados:', ticketData)
          const { data: newTicket } = await api.post('/tickets', ticketData)

          console.log('Ticket criado:', newTicket)

          // Navegar para o ticket criado
          history.push(`/tickets/${newTicket.uuid || newTicket.id}`)
        } catch (error) {
          console.error('Erro ao criar ticket:', error)

          // Se o erro for relacionado a ticket já existente, tentar encontrar o ticket
          if (
            error.response &&
            error.response.data &&
            error.response.data.message &&
            error.response.data.message.includes('ticket')
          ) {
            try {
              // Buscar novamente o contato e seus tickets
              const { data: contactsData } = await api.get('/contacts/', {
                params: {
                  searchParam: number.replace(/\D/g, ''),
                  pageNumber: 1
                }
              })

              if (contactsData.contacts && contactsData.contacts.length > 0) {
                const contactId = contactsData.contacts[0].id
                const { data: ticketsData } = await api.get('/tickets/', {
                  params: {
                    contactId: contactId,
                    pageNumber: 1
                  }
                })

                if (ticketsData.tickets && ticketsData.tickets.length > 0) {
                  // Navegar para o ticket mais recente
                  const mostRecentTicket = ticketsData.tickets[0]
                  console.log(
                    'Navegando para ticket mais recente:',
                    mostRecentTicket
                  )
                  history.push(
                    `/tickets/${mostRecentTicket.uuid || mostRecentTicket.id}`
                  )
                  return
                }
              }
            } catch (findTicketError) {
              console.error('Erro ao buscar ticket existente:', findTicketError)
            }
          }

          toastError(error)
        }
      }

      return (
        <div key={metaNumber}>
          <div className={classes.vCardContainer}>
            {/* Header com avatar e informações */}
            <div className={classes.vCardHeader}>
              <Avatar
                className={classes.vCardAvatar}
                style={{
                  backgroundColor: generateColor(metaNumber),
                  width: 48,
                  height: 48,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '18px'
                }}
              >
                {getInitials(name)}
              </Avatar>
              <div className={classes.vCardInfo}>
                <Typography
                  component="h3"
                  variant="body1"
                  className={classes.vCardName}
                >
                  {name}
                </Typography>
                <div className={classes.vCardPhoneContainer}>
                  <span className={classes.vCardPhoneIcon}>📱</span>
                  <Typography
                    component="span"
                    variant="body2"
                    className={classes.vCardPhone}
                  >
                    {number}
                  </Typography>
                </div>
                {description && (
                  <Typography
                    component="p"
                    variant="body2"
                    className={classes.vCardDescription}
                  >
                    {description}
                  </Typography>
                )}
              </div>
            </div>

            {/* Botões de ação */}
            <div className={classes.vCardAction}>
              <button
                className={classes.vCardActionButton}
                onClick={handleAddContact}
              >
                {/* <span className={classes.vCardActionIcon}>👤</span> */}
                <Typography
                  component="span"
                  variant="body2"
                  className={classes.vCardActionText}
                >
                  Adicionar contato
                </Typography>
              </button>

              <button
                className={`${classes.vCardActionButton} ${classes.vCardActionSecondary}`}
                onClick={handleCreateTicket}
              >
                {/* <span className={classes.vCardActionIconSecondary}>💬</span> */}
                <Typography
                  component="span"
                  variant="body2"
                  className={classes.vCardActionTextSecondary}
                >
                  Conversar
                </Typography>
              </button>
            </div>
          </div>
        </div>
      )
    })
  }

  const messageLocation = (message, createdAt) => {
    return (
      <div
        className={[classes.textContentItem, { display: 'flex', padding: 5 }]}
      >
        <img
          src={message.split('|')[0]}
          className={classes.imageLocation}
          alt={message}
        />
        <a
          style={{ fontWeight: '700', color: 'gray' }}
          target="_blank"
          href={message.split('|')[1]}
          rel="noreferrer"
        >
          {' '}
          Clique para ver localização
        </a>
        <span className={classes.timestamp}>
          {format(parseISO(createdAt), 'HH:mm')}
        </span>
      </div>
    )
  }

  const getDataContextInfo = data => {
    if (!data) {
      return null
    }

    return (
      data.message?.extendedTextMessage?.contextInfo ||
      data.message?.imageMessage?.contextInfo ||
      data.message?.videoMessage?.contextInfo ||
      data.message?.audioMessage?.contextInfo ||
      data.message?.documentMessage?.contextInfo ||
      data.message?.stickerMessage?.contextInfo ||
      data.message?.productMessage?.contextInfo ||
      data.message?.locationMessage?.contextInfo ||
      data.message?.liveLocationMessage?.contextInfo ||
      data.message?.contactMessage?.contextInfo ||
      data.message?.listMessage?.contextInfo ||
      data.message?.buttonsResponseMessage?.contextInfo ||
      data.message?.paymentMessage?.contextInfo ||
      data.message?.orderMessage?.contextInfo ||
      data.message?.productCatalogMessage?.contextInfo ||
      data.message?.templateButtonReplyMessage?.contextInfo ||
      data.message?.templateMessage?.contextInfo ||
      data.message?.documentWithCaptionMessage?.contextInfo ||
      null
    )
  }

  const handleInternalMessage = message => {
    let name = null
    let messageText = message.body

    if (typeof message.body !== 'string') {
      return { name: null, messageText: message.body }
    }
    const textBody = message.body.split('*')
    if (
      textBody.length >= 3 &&
      textBody[0] === '' &&
      textBody[2].trim().length > 0
    ) {
      name = textBody[1].replace(/:$/, '').trim()
      messageText = textBody.slice(2).join('*').trim()
    }
    return { name, messageText }
  }

  const renderMessages = () => {
    return allTimelineItems.map((item, index) => {
      if (item.isSystemEvent) {
        return (
          <React.Fragment key={`system-event-${item.id}`}>
            {renderDailyTimestamps(item, index)}
            <SystemEventBadge event={item} classes={classes} />
          </React.Fragment>
        )
      }

      const message = item

      const isReactionMenuOpenForThis =
        reactionsMenuOpen && selectedReactionMessage?.id === message.id
      const showReactionsButton =
        (hoveredMessageId === message.id || isReactionMenuOpenForThis) &&
        !message.isDeleted

      const isActionMenuOpenForThis =
        actionsMenuOpen && selectedActionMessage?.id === message.id

      if (message.mediaType === 'reactionMessage') {
        return null
      }
      const data = JSON.parse(message.dataJson)
      const dataContext = getDataContextInfo(data)
      const messageError = getMessageErrorData(message)
      const isSticker = data?.message && 'stickerMessage' in data.message

      if (message.channel === 'internal') {
        const { name, messageText } = handleInternalMessage(message)
        const isSystemNote = name?.trim().toLowerCase() === 'sistema'
        return (
          <div key={message.id} className={classes.boxMessageInternal}>
            {renderDailyTimestamps(message, index)}
            {renderMessageDivider(message, index)}
            <div
              id={message.id}
              className={clsx(classes.messageInternal, {
                [classes.messageInternalSystem]: isSystemNote
              })}
              title={message.queueId && message.queue?.name}
            >
              <div className={classes.boxContentMessageInternal}>
                <div className={classes.textContentItem}>
                  <div className={classes.textContentMesssageInternal}>
                    <MessageSquareLock size={18} />
                    <span className={classes.messageInternalName}>{name}</span>
                  </div>
                  <WhatsMarked>{messageText}</WhatsMarked>
                </div>
              </div>
            </div>
            <div className={classes.timestampRightInternal}>
              <span className={classes.timestamp}>
                {format(parseISO(message.createdAt), 'dd/MM/yy')}
              </span>
              •
              <span className={classes.timestamp}>
                {format(parseISO(message.createdAt), 'HH:mm')}
              </span>
            </div>
          </div>
        )
      }

      if (!message.fromMe) {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderMessageDivider(message, index)}
            <div
              className={classes.boxMessageLeft}
              onMouseEnter={() => setHoveredMessageId(message.id)}
              onMouseLeave={() => setHoveredMessageId(null)}
            >
              <div className={classes.contentMessageLeft}>
                <div
                  id={message.id}
                  className={[
                    clsx(classes.messageContainer, classes.messageLeft, {
                      [classes.messageMediaSticker]: isSticker,
                      [classes.messageContainerWithReactions]:
                        message.replies &&
                        message.replies.some(
                          reply => reply.mediaType === 'reactionMessage'
                        )
                    })
                  ]}
                  title={message.queueId && message.queue?.name}
                >
                  {!message.isDeleted && (
                    <button
                      id={`messageActionsButton-${message.id}`}
                      disabled={message.isDeleted}
                      className={clsx(classes.messageActionsButton, {
                        [classes.messageActionsButtonOpen]:
                          isActionMenuOpenForThis
                      })}
                      onClick={e => handleOpenActionsMenu(e, message, data)}
                    >
                      <ExpandMore />
                    </button>
                  )}
                  {dataContext?.isForwarded && (
                    <div className={classes.forwardedMessage}>
                      <Forward
                        fontSize="small"
                        className={classes.forwardedIcon}
                      />
                      {/* <span> {i18n.t('message.forwarded')} </span> */}
                      <span> Encaminhada </span>
                    </div>
                  )}
                  {getIncomingMessageSignature(message) && (
                    <span className={classes.messageContactName}>
                      {getIncomingMessageSignature(message)}
                    </span>
                  )}

                  {/* {message.thumbnailUrl && (
                  <img
                    className={classes.previewThumbnail}
                    src={message.thumbnailUrl}
                  />
                )} */}

                  {message.body.includes('data:image') ? (
                    messageLocation(message.body, message.createdAt)
                  ) : isVCard(message.body) ? (
                    <div
                      className={[
                        clsx(
                          classes.textContentItem,
                          classes.textContentItemIsVCard,
                          {
                            [classes.textContentItemEdited]: message.isEdited
                          }
                        )
                        // { marginRight: 0 }
                      ]}
                    >
                      {renderVCard(message.body)}
                    </div>
                  ) : (
                    <div
                      className={[
                        clsx(classes.textContentItem, {
                          [classes.textContentItemDeleted]: message.isDeleted,
                          [classes.textContentItemEdited]: message.isEdited
                        })
                      ]}
                    >
                      {message.quotedMsg && renderQuotedMessage(message)}
                      {renderLinkPreview(message)}
                      {!isSticker &&
                        (message.mediaUrl &&
                          !data?.message?.extendedTextMessage ? (
                          ''
                        ) : (
                          <>
                            {message.isDeleted && (
                              <Ban className={classes.deletedIcon} />
                            )}
                            {!isVCard(message.body) && (
                              <WhatsMarked>{message.body}</WhatsMarked>
                            )}
                          </>
                        ))}
                    </div>
                  )}
                  {message.mediaUrl &&
                    !data?.message?.extendedTextMessage &&
                    checkMessageMedia(message, data)}
                  {/* {renderReplies(message.replies)} */}
                  {renderReplies(message.replies, false)}
                </div>

                <div
                  // className={[
                  //   clsx(classes.timestamp, {
                  //     [classes.timestampStickerLeft]: isSticker
                  //   })
                  // ]}
                  className={classes.timestamp}
                >
                  {message.isEdited && (
                    <>
                      <span className={classes.timestampLeft}>
                        {i18n.t('message.edited')}
                      </span>
                      •
                    </>
                  )}
                  <div className={classes.timestampLeft}>
                    <span>
                      {format(parseISO(message.createdAt), 'dd/MM/yy')}
                    </span>
                    •
                    <span> {format(parseISO(message.createdAt), 'HH:mm')}</span>
                  </div>
                </div>
              </div>
              {showReactionsButton && (
                <div className={classes.reactionContainerLeft}>
                  <IconButton
                    aria-label={`Adicionar Reação`}
                    aria-controls={
                      isReactionMenuOpenForThis ? 'reaction-menu' : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={
                      isReactionMenuOpenForThis ? 'true' : undefined
                    }
                    className={clsx(
                      classes.reactionButton,
                      'reactionButtonClass'
                    )}
                    onClick={e => {
                      e.stopPropagation()
                      handleOpenReactionsMenu(e, message)
                    }}
                  >
                    <Smile size={16} />
                  </IconButton>
                </div>
              )}
            </div>
          </React.Fragment>
        )
      } else {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderMessageDivider(message, index)}
            <div
              className={classes.boxMessageRight}
              onMouseEnter={() => setHoveredMessageId(message.id)}
              onMouseLeave={() => setHoveredMessageId(null)}
            >
              {showReactionsButton && (
                <div className={classes.reactionContainerRight}>
                  <IconButton
                    aria-label={`Adicionar Reação`}
                    aria-controls={
                      isReactionMenuOpenForThis ? 'reaction-menu' : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={
                      isReactionMenuOpenForThis ? 'true' : undefined
                    }
                    className={clsx(
                      classes.reactionButton,
                      'reactionButtonClass'
                    )}
                    onClick={e => {
                      e.stopPropagation()
                      handleOpenReactionsMenu(e, message)
                    }}
                  >
                    <Smile size={16} />
                  </IconButton>
                </div>
              )}
              <div className={classes.contentMessageRight}>
                <div
                  id={message.id}
                  className={[
                    clsx(classes.messageContainer, classes.messageRight, {
                      [classes.messageMediaSticker]: isSticker,
                      [classes.messageContainerWithReactions]:
                        message.replies &&
                        message.replies.some(
                          reply => reply.mediaType === 'reactionMessage'
                        )
                    })
                  ]}
                  title={message.queueId && message.queue?.name}
                >
                  {!message.isDeleted && (
                    <button
                      id={`messageActionsButton-${message.id}`}
                      disabled={message.isDeleted}
                      className={classes.messageActionsButton}
                      onClick={e => handleOpenActionsMenu(e, message, data)}
                    >
                      <ExpandMore />
                    </button>
                  )}
                  {dataContext?.isForwarded && (
                    <span className={classes.forwardedMessage}>
                      <Forward
                        fontSize="small"
                        className={classes.forwardedIcon}
                      />
                      {/* {i18n.t('message.forwarded') } */} Encaminhada
                    </span>
                  )}
                  {/* 
                  {message.thumbnailUrl && (
                    <img
                      className={classes.previewThumbnail}
                      src={message.thumbnailUrl}
                      alt={message.thumbnailUrl}
                    />
                  )} */}

                  <div
                    className={clsx(classes.textContentItem, {
                      [classes.textContentItemDeleted]: message.isDeleted,
                      [classes.textContentItemEdited]: message.isEdited
                    })}
                  >
                    {message.isDeleted && (
                      <Ban className={classes.deletedIcon} />
                    )}

                    {message.body.includes('data:image') ? (
                      messageLocation(message.body, message.createdAt)
                    ) : isVCard(message.body) ? (
                      <div className={classes.textContentItemIsVCard}>
                        {renderVCard(message.body)}
                      </div>
                    ) : (
                      message.quotedMsg && renderQuotedMessage(message)
                    )}
                    {renderLinkPreview(message)}
                    {!isSticker &&
                      (message.mediaUrl ? (
                        ''
                      ) : !isVCard(message.body) ? (
                        <WhatsMarked>{message.body}</WhatsMarked>
                      ) : null)}
                  </div>
                  {message.mediaUrl && checkMessageMedia(message, data)}
                  {/* {renderReplies(message.replies)} */}
                  {renderReplies(message.replies, true)}
                </div>
                <div
                  // className={[
                  //   clsx(classes.timestamp, {
                  //     [classes.timestampStickerRight]: isSticker
                  //   })
                  // ]}
                  className={classes.timestamp}
                >
                  {message.isEdited && (
                    <>
                      <span> {i18n.t('message.edited')} </span>•
                    </>
                  )}
                  <div className={classes.timestampRight}>
                    <span>
                      {format(parseISO(message.createdAt), 'dd/MM/yy')}
                    </span>
                    •
                    <span> {format(parseISO(message.createdAt), 'HH:mm')}</span>
                    {renderMessageAck(message)}
                  </div>
                </div>
                {messageError && (
                  <div
                    className={classes.messageErrorBand}
                    title={messageError.title}
                  >
                    {messageError.code} - {messageError.message}
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        )
      }
    })
    // return viewMessagesList
  }

  return (
    <div className={classes.messagesListWrapper}>
      <MessageReactionsMenu
        message={selectedReactionMessage}
        menuOpen={reactionsMenuOpen}
        handleClose={handleCloseReactionsMenu}
        anchorEl={reactionsAnchorEl}
      />

      <MessageActionsMenu
        message={selectedActionMessage}
        data={selectedActionMessageData}
        menuOpen={actionsMenuOpen}
        handleClose={handleCloseActionsMenu}
        anchorEl={actionsAnchorEl}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
        ref={scrollRef}
      >
        {messagesList.length > 0 ? renderMessages() : []}
        {contactPresence === 'composing' && (
          <div className={classes.messageLeftWave} data-testid="composing-wave">
            <div className={classes.wave}>
              <span className={classes.dot}></span>
              <span className={classes.dot}></span>
              <span className={classes.dot}></span>
            </div>
          </div>
        )}
        {contactPresence === 'recording' && (
          <div className={classes.messageLeft}>
            <div className={classes.wavebarsContainer}>
              <div className={clsx(classes.wavebars, classes.wavebar1)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar2)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar3)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar4)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar5)}></div>
            </div>
          </div>
        )}
      </div>
      {ticket?.channel !== 'whatsapp' ||
        (ticket.channel === undefined && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              padding: '10px',
              alignItems: 'center',
              backgroundColor: '#E1F3FB'
            }}
          >
            {ticket?.channel === 'facebook' ? (
              <Facebook small />
            ) : (
              <Instagram small />
            )}

            <span>
              Você tem 24h para responder após receber uma mensagem, de acordo
              com as políticas do Facebook.
            </span>
          </div>
        ))}
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  )
})

export default MessagesList
